// Linehaul (LH) schedule data access. The ONLY place that talks to the
// `lh_schedule` table. A schedule is a set of weekday rows for one customer→hub
// lane.

import { supabase } from "../../lib/supabase";
import { isValidTimeHHMM, parseOptionalNumber } from "../../lib/validation";
import type { LhScheduleDay, TrailerType } from "../../lib/types";

interface LhRow {
  id: string;
  account_id: string;
  customer_id: string;
  hub_id: string;
  day_of_week: number;
  loads: number;
  trailer_type: string | null;
  arrival_times: unknown; // jsonb -> string[]
}

/** DB row -> app LhScheduleDay. */
export function rowToLhDay(row: LhRow): LhScheduleDay {
  const times = Array.isArray(row.arrival_times) ? (row.arrival_times as string[]) : [];
  return {
    dayOfWeek: row.day_of_week,
    loads: Number(row.loads),
    trailerType: (row.trailer_type as TrailerType) ?? undefined,
    arrivalTimes: times,
  };
}

/** App LhScheduleDay -> DB row fields for a given lane. */
export function lhDayInputToRow(
  accountId: string,
  customerId: string,
  hubId: string,
  day: LhScheduleDay
) {
  return {
    account_id: accountId,
    customer_id: customerId,
    hub_id: hubId,
    day_of_week: day.dayOfWeek,
    loads: day.loads,
    trailer_type: day.trailerType ?? null,
    arrival_times: day.arrivalTimes,
  };
}

/** True when a day actually carries linehaul activity (worth persisting). */
export function dayHasActivity(day: LhScheduleDay): boolean {
  return day.loads > 0 || day.arrivalTimes.length > 0 || !!day.trailerType;
}

/**
 * Each load arrives exactly once, so a day's arrival-time count must equal its
 * loads (2 loads need 2 times; 1 load can't have 2 times and vice-versa).
 */
export function timesMatchLoads(loads: number, timeCount: number): boolean {
  return timeCount === loads;
}

/**
 * Parse a free-text list of arrival times ("08:00, 14:30") into a validated
 * array. Returns null if any entry is not a valid HH:MM. Empty -> [].
 */
export function parseTimeList(raw: string): string[] | null {
  const parts = raw
    .split(",")
    .map((p) => p.trim())
    .filter((p) => p !== "");
  for (const p of parts) {
    if (!isValidTimeHHMM(p)) return null;
  }
  return parts;
}

/** One stored schedule row tagged with its lane (customer + hub). */
export interface LhLaneRow {
  customerId: string;
  hubId: string;
  day: LhScheduleDay;
}

/** Load every schedule row in the account (for network/loads computations). */
export async function listAccountLhSchedule(accountId: string): Promise<LhLaneRow[]> {
  const { data, error } = await supabase
    .from("lh_schedule")
    .select("*")
    .eq("account_id", accountId);
  if (error) throw error;
  return (data as LhRow[]).map((r) => ({
    customerId: r.customer_id,
    hubId: r.hub_id,
    day: rowToLhDay(r),
  }));
}

/** Load every schedule row for one customer (across all their hub lanes). */
export async function listCustomerLhSchedule(
  accountId: string,
  customerId: string
): Promise<LhLaneRow[]> {
  const { data, error } = await supabase
    .from("lh_schedule")
    .select("*")
    .eq("account_id", accountId)
    .eq("customer_id", customerId);
  if (error) throw error;
  return (data as LhRow[]).map((r) => ({
    customerId: r.customer_id,
    hubId: r.hub_id,
    day: rowToLhDay(r),
  }));
}

/** Editable form state for one weekday (raw strings; validated by buildWeekDays). */
export interface WeekDayDraft {
  loads: string;
  trailerType: string;
  arrivalTimes: string; // comma-separated "HH:MM"
}

export type WeekBuildResult =
  | { ok: true; days: LhScheduleDay[] }
  | { ok: false; dayIndex: number; message: string };

/**
 * Validate + convert a 7-row weekly grid (index = day_of_week, 0=Mon..6=Sun)
 * into LhScheduleDay[]. Returns the offending day + message on failure.
 */
export function buildWeekDays(grid: WeekDayDraft[]): WeekBuildResult {
  const days: LhScheduleDay[] = [];
  for (let i = 0; i < grid.length; i++) {
    const r = grid[i];
    const loadsP = parseOptionalNumber(r.loads);
    if (!loadsP.ok || (loadsP.value !== null && loadsP.value < 0)) {
      return { ok: false, dayIndex: i, message: "loads must be a positive number." };
    }
    const loadsVal = loadsP.value === null ? 0 : Math.round(loadsP.value);
    const times = parseTimeList(r.arrivalTimes);
    if (times === null) {
      return { ok: false, dayIndex: i, message: "arrival times must be HH:MM, comma-separated." };
    }
    if (!timesMatchLoads(loadsVal, times.length)) {
      return {
        ok: false,
        dayIndex: i,
        message: `needs ${loadsVal} arrival time(s) to match ${loadsVal} load(s), but has ${times.length}.`,
      };
    }
    days.push({
      dayOfWeek: i,
      loads: loadsVal,
      trailerType: r.trailerType === "" ? undefined : (r.trailerType as TrailerType),
      arrivalTimes: times,
    });
  }
  return { ok: true, days };
}

/** Load the schedule rows for one customer→hub lane. */
export async function listLhSchedule(
  accountId: string,
  customerId: string,
  hubId: string
): Promise<LhScheduleDay[]> {
  const { data, error } = await supabase
    .from("lh_schedule")
    .select("*")
    .eq("account_id", accountId)
    .eq("customer_id", customerId)
    .eq("hub_id", hubId)
    .order("day_of_week");
  if (error) throw error;
  return (data as LhRow[]).map(rowToLhDay);
}

/**
 * Replace the whole weekly schedule for a lane: delete existing rows, then insert
 * the days that carry activity. Scoped by account + lane.
 */
export async function saveLhSchedule(
  accountId: string,
  customerId: string,
  hubId: string,
  days: LhScheduleDay[]
): Promise<void> {
  const { error: delErr } = await supabase
    .from("lh_schedule")
    .delete()
    .eq("account_id", accountId)
    .eq("customer_id", customerId)
    .eq("hub_id", hubId);
  if (delErr) throw delErr;

  const rows = days.filter(dayHasActivity).map((d) => lhDayInputToRow(accountId, customerId, hubId, d));
  if (rows.length === 0) return;
  const { error } = await supabase.from("lh_schedule").insert(rows);
  if (error) throw error;
}
